import apiClient from './apiClient';

export interface SyncJob {
  jobId: string;
  userId: string;
  type: 'plan-change' | 'week-sync';
  payload: {
    userId: string;
    type: 'plan-change' | 'week-sync';
    oldPlanId?: string;
    newPlanId?: string;
    weekNumber?: number;
    trigger?: 'workout_move' | 'visibility_toggle' | 'plan_change';
    workoutId?: string;
  };
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: string;
  error?: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  retryCount: number;
}

export interface SyncJobsResponse {
  jobs: SyncJob[];
}

export interface SyncJobStats {
  total: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}

export const syncService = {
  async getSyncJobStatus(jobId: string): Promise<SyncJob> {
    const response = await apiClient.get(`/sync/job/${jobId}`);
    return response.data;
  },

  async getUserSyncJobs(activeOnly: boolean = true): Promise<SyncJob[]> {
    const response = await apiClient.get<SyncJobsResponse>('/sync/jobs', {
      params: { active: activeOnly },
    });
    return response.data.jobs;
  },

  async getSyncJobStats(): Promise<SyncJobStats> {
    const response = await apiClient.get<SyncJobStats>('/sync/jobs/stats');
    return response.data;
  },

  async waitForJobCompletion(
    jobId: string,
    pollIntervalMs: number = 2000,
    maxAttempts: number = 60
  ): Promise<SyncJob> {
    let attempts = 0;

    while (attempts < maxAttempts) {
      const job = await this.getSyncJobStatus(jobId);

      if (job.status === 'completed' || job.status === 'failed') {
        return job;
      }

      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
      attempts++;
    }

    throw new Error(`Job ${jobId} timed out after ${maxAttempts} attempts`);
  },
};

export default syncService;
